import { useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import {
  useInvitesQuery,
  useCreateInviteMutation,
  useRevokeInviteMutation,
} from "@/api/invites";
import {
  useTeamByIdQuery,
  useTeamMembersQuery,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useUpdateMemberRoleMutation,
  useSetTeamApiKeyMutation,
  useDeleteTeamApiKeyMutation,
  useTeamApiKeyStatusQuery,
  useSetMemberTeamKeyMutation,
} from "@/api/teams";
import { Layout } from "@/components/layout/Layout";
import type { TeamMember } from "@/types/team";

export function TeamSettingsPage({ teamId }: { teamId: string }) {
  const router = useRouter();
  const user = useAuthStore((s) => s.user);
  const { data: team, isLoading } = useTeamByIdQuery(teamId);
  const { data: members } = useTeamMembersQuery(teamId);
  const { data: apiKeyStatus } = useTeamApiKeyStatusQuery(teamId);

  const updateTeam = useUpdateTeamMutation(teamId);
  const deleteTeam = useDeleteTeamMutation();
  const addMember = useAddTeamMemberMutation(teamId);
  const removeMember = useRemoveTeamMemberMutation(teamId);
  const updateRole = useUpdateMemberRoleMutation(teamId);
  const setApiKey = useSetTeamApiKeyMutation(teamId);
  const deleteApiKey = useDeleteTeamApiKeyMutation(teamId);
  const setMemberTeamKey = useSetMemberTeamKeyMutation(teamId);

  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editing, setEditing] = useState(false);
  const [newMemberUsername, setNewMemberUsername] = useState("");
  const [newMemberUseTeamKey, setNewMemberUseTeamKey] = useState(false);
  const [teamApiKey, setTeamApiKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  const myRole = members?.find((m: TeamMember) => m.user_id === user?.id)?.role;
  const canManage = myRole === "owner" || myRole === "admin";
  const isOwner = myRole === "owner";

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <span className="text-sm text-ink-3">Loading team...</span>
        </div>
      </Layout>
    );
  }

  if (!team) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <span className="text-sm text-ink-3">Team not found</span>
        </div>
      </Layout>
    );
  }

  function startEditing() {
    if (!team) return;
    setEditName(team.name);
    setEditSlug(team.slug);
    setEditing(true);
  }

  async function saveTeam() {
    setError(null);
    try {
      await updateTeam.mutateAsync({ name: editName, slug: editSlug });
      setEditing(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update team");
    }
  }

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this team?")) return;
    try {
      await deleteTeam.mutateAsync(teamId);
      void router.navigate({ to: "/" });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete team");
    }
  }

  async function handleAddMember() {
    setError(null);
    if (!newMemberUsername.trim()) return;
    try {
      await addMember.mutateAsync({
        username: newMemberUsername.trim(),
        use_team_key: newMemberUseTeamKey,
      });
      setNewMemberUsername("");
      setNewMemberUseTeamKey(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add member");
    }
  }

  async function handleSetApiKey() {
    setError(null);
    if (!teamApiKey.trim()) return;
    try {
      await setApiKey.mutateAsync(teamApiKey.trim());
      setTeamApiKey("");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to set API key");
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-2xl space-y-8 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-ink">
            Team Settings
          </h1>
          <Link
            to="/"
            className="text-sm text-ink-2 hover:text-ink"
          >
            Back
          </Link>
        </div>

        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        {/* Team Info */}
        <section className="rounded-xl border border-line bg-surface-2 p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">
            General
          </h2>
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  Slug
                </label>
                <input
                  type="text"
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value)}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => void saveTeam()}
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-ink">
                <span className="text-ink-2">Name:</span> {team.name}
              </p>
              <p className="text-sm text-ink">
                <span className="text-ink-2">Slug:</span> {team.slug}
              </p>
              {team.is_personal && (
                <p className="text-xs text-ink-3">
                  This is your personal team and cannot be modified.
                </p>
              )}
              {canManage && !team.is_personal && (
                <button
                  onClick={startEditing}
                  className="mt-2 text-sm text-accent hover:underline"
                >
                  Edit
                </button>
              )}
            </div>
          )}
        </section>

        {/* API Key */}
        <section className="rounded-xl border border-line bg-surface-2 p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">
            Team API Key
          </h2>
          <p className="mb-4 text-xs text-ink-2">
            Shared Anthropic API key for all team members. Individual members can
            override with their own key.
          </p>
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${
                apiKeyStatus?.has_key ? "bg-success" : "bg-ink-3"
              }`}
            />
            <span className="text-sm text-ink">
              {apiKeyStatus?.has_key ? "Key configured" : "No key set"}
            </span>
          </div>
          {canManage && (
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <input
                  type="password"
                  value={teamApiKey}
                  onChange={(e) => setTeamApiKey(e.target.value)}
                  placeholder="sk-ant-..."
                  className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                />
                <button
                  onClick={() => void handleSetApiKey()}
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
                >
                  Set
                </button>
              </div>
              {apiKeyStatus?.has_key && (
                <button
                  onClick={() => void deleteApiKey.mutateAsync()}
                  className="text-xs text-danger hover:underline"
                >
                  Remove team key
                </button>
              )}
            </div>
          )}
        </section>

        {/* Members */}
        <section className="rounded-xl border border-line bg-surface-2 p-6">
          <h2 className="mb-4 text-lg font-semibold text-ink">
            Members
          </h2>
          <div className="space-y-2">
            {members?.map((member: TeamMember) => (
              <div
                key={member.user_id}
                className="flex items-center justify-between rounded-lg border border-line bg-surface px-4 py-2"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm text-ink">
                    {member.username}
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      member.role === "owner"
                        ? "bg-accent-soft text-accent"
                        : member.role === "admin"
                          ? "bg-surface-3 text-ink"
                          : "bg-surface-3 text-ink-3"
                    }`}
                  >
                    {member.role}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Team key access toggle */}
                  {canManage && member.role !== "owner" && (
                    <label className="flex items-center gap-1.5 cursor-pointer" title={member.use_team_key ? "Can use team API key" : "Must use own API key"}>
                      <input
                        type="checkbox"
                        checked={member.use_team_key}
                        onChange={(e) =>
                          void setMemberTeamKey.mutateAsync({
                            userId: member.user_id,
                            useTeamKey: e.target.checked,
                          })
                        }
                        className="accent-accent"
                      />
                      <span className="text-[10px] text-ink-3">
                        Team key
                      </span>
                    </label>
                  )}
                  {member.role === "owner" && (
                    <span className="text-[10px] text-ink-3">
                      Team key
                    </span>
                  )}
                  {isOwner && member.role !== "owner" && (
                    <>
                      <select
                        value={member.role}
                        onChange={(e) =>
                          void updateRole.mutateAsync({
                            userId: member.user_id,
                            role: e.target.value,
                          })
                        }
                        className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink focus:border-line-strong focus:outline-none"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <button
                        onClick={() =>
                          void removeMember.mutateAsync(member.user_id)
                        }
                        className="text-xs text-danger hover:underline"
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {canManage && (
            <div className="mt-4 space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMemberUsername}
                  onChange={(e) => setNewMemberUsername(e.target.value)}
                  placeholder="Username to add..."
                  className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleAddMember();
                  }}
                />
                <button
                  onClick={() => void handleAddMember()}
                  className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
                >
                  Add
                </button>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newMemberUseTeamKey}
                  onChange={(e) => setNewMemberUseTeamKey(e.target.checked)}
                  className="accent-accent"
                />
                <span className="text-xs text-ink-2">
                  Allow use of team API key
                </span>
              </label>
            </div>
          )}
        </section>

        {/* Invite Codes */}
        {canManage && !team.is_personal && (
          <InviteSection teamId={team.id} teamName={team.name} />
        )}

        {/* Danger Zone */}
        {isOwner && !team.is_personal && (
          <section className="rounded-xl border border-danger/30 bg-surface-2 p-6">
            <h2 className="mb-4 text-lg font-semibold text-danger">
              Danger Zone
            </h2>
            <p className="mb-4 text-sm text-ink-2">
              Deleting a team is permanent. All team templates will become
              unscoped.
            </p>
            <button
              onClick={() => void handleDelete()}
              className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
            >
              Delete Team
            </button>
          </section>
        )}
      </div>
    </Layout>
  );
}

function InviteSection({ teamId, teamName }: { teamId: string; teamName: string }) {
  const { data: invites } = useInvitesQuery();
  const createInvite = useCreateInviteMutation();
  const revokeInvite = useRevokeInviteMutation();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [maxUses, setMaxUses] = useState<string>("");
  const [copied, setCopied] = useState<string | null>(null);

  const teamInvites = (invites ?? []).filter(
    (i) => i.team_id === teamId,
  );

  function handleCreate() {
    createInvite.mutate({
      type: "both",
      team_id: teamId,
      email: inviteEmail.trim() || undefined,
      team_role: inviteRole,
      max_uses: maxUses ? Number(maxUses) : undefined,
    });
    setInviteEmail("");
    setMaxUses("");
  }

  function handleCopy(code: string) {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <section className="rounded-xl border border-line bg-surface-2 p-6">
      <h2 className="mb-4 text-lg font-semibold text-ink">
        Invite Codes
      </h2>

      {/* Create form */}
      <div className="mb-4 space-y-3 rounded-lg border border-line bg-surface p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              Email (optional — leave blank for anyone)
            </label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              Team role
            </label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-line-strong focus:outline-none"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              Max uses (blank = unlimited)
            </label>
            <input
              type="number"
              min="1"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              className="w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={createInvite.isPending}
          className="rounded-lg bg-accent px-4 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
        >
          {createInvite.isPending ? "Creating..." : "Create Invite"}
        </button>
      </div>

      {/* Active invites */}
      {teamInvites.length === 0 ? (
        <p className="text-xs text-ink-3">
          No active invites for {teamName}.
        </p>
      ) : (
        <div className="space-y-2">
          {teamInvites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between rounded-lg border border-line bg-surface px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleCopy(inv.code)}
                  className="rounded bg-accent-soft px-2 py-1 font-mono text-sm font-medium text-accent transition-colors hover:bg-surface-3"
                  title="Click to copy"
                >
                  {copied === inv.code ? "Copied!" : inv.code}
                </button>
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="rounded border border-line bg-surface-3 px-1.5 py-0.5 text-[10px] text-ink-3">
                      {inv.type}
                    </span>
                    <span className="text-[10px] text-ink-3">
                      role: {inv.team_role}
                    </span>
                    {inv.email && (
                      <span className="text-[10px] text-ink-3">
                        for: {inv.email}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-ink-3">
                    {inv.use_count}{inv.max_uses ? `/${inv.max_uses}` : ""} used
                    {inv.expires_at && ` · expires ${new Date(inv.expires_at).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => revokeInvite.mutate(inv.id)}
                className="text-xs text-danger/60 hover:text-danger"
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
